/**
 * Lift2Fit — admin: Revenue (`#/admin/revenue`).
 *
 * The platform's revenue story from the payments ledger, by period. This is the
 * CRM ledger read across every business, plus the businesses that produced the
 * money. No invoices, no tax, no per-client detail.
 */
import { useState } from 'react';
import { api } from '@/lib/api';
import { formatCompactCount, formatCompactMoney, formatCount, formatDate } from '@/lib/format';
import { AlertIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  MetricTile,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { formatMoney, toDateInput } from '../crm/shared';
import { useAsync } from '../hooks';
import { Link } from '../router';
import { ADMIN_EYEBROW, coachPlanLabel } from './shared';

function monthStart(): string {
  const now = new Date();
  return toDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
}

export function AdminRevenueScreen() {
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(() => toDateInput(new Date()));
  const state = useAsync(() => api.adminRevenue(from, to), [from, to]);
  const summaryState = useAsync(() => api.adminSummary(), []);
  const premiumState = useAsync(() => api.adminAccounts({ role: 'lifter', premium: true, limit: 8 }), []);
  const businessState = useAsync(() => api.adminBusinesses(), []);
  const revenue = state.data;
  const subscriptions = summaryState.data?.revenue.subscriptions ?? null;
  const currency = summaryState.data?.revenue.currency ?? revenue?.currency ?? 'GBP';
  const premiumLifters = premiumState.data?.data ?? [];
  const paidBusinesses = (businessState.data?.data ?? []).filter(
    (business) => business.plan === 'coach' || business.plan === 'gym' || business.plan === 'gym_plus',
  );

  if (state.loading && !revenue) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading the ledger" rows={4} />
        <LoadingPanel label="Loading the period series" rows={3} />
      </div>
    );
  }

  if (!revenue) {
    return (
      <EmptyState
        title="We could not read the revenue"
        body={state.error ?? 'The admin API did not answer. Sign in as an administrator and try again.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/admin" variant="outline">
            Back to the overview
          </AppLinkButton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="admin-revenue">
      <ScreenHeader
        eyebrow={`${ADMIN_EYEBROW} · money`}
        title="Revenue"
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead="The payments ledger across every coaching business, by period: what has been paid, what is due and what has been refunded."
      />

      <Panel
        title="Range"
        subtitle={`${formatDate(revenue.from)} – ${formatDate(revenue.to)} · ${revenue.currency}`}
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">From</span>
              <input
                type="date"
                data-testid="admin-revenue-from"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
              />
            </label>
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">To</span>
              <input
                type="date"
                data-testid="admin-revenue-to"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
              />
            </label>
          </div>
        }
      >
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <MetricTile
            label="Paid"
            value={formatCompactMoney(revenue.paid.total, revenue.currency)}
            valueTitle={formatMoney(revenue.paid.total, revenue.currency)}
            hint={`${formatCount(revenue.paid.count)} payment(s)`}
            hintTone="success"
            valueTestId="admin-revenue-tile-paid"
          />
          <MetricTile
            label="Due"
            value={formatCompactMoney(revenue.due.total, revenue.currency)}
            valueTitle={formatMoney(revenue.due.total, revenue.currency)}
            hint={`${formatCount(revenue.due.count)} outstanding`}
            hintTone={revenue.due.total > 0 ? 'warning' : 'success'}
            valueTestId="admin-revenue-tile-due"
          />
          <MetricTile
            label="Refunded"
            value={formatCompactMoney(revenue.refunded.total, revenue.currency)}
            valueTitle={formatMoney(revenue.refunded.total, revenue.currency)}
            hint={`${formatCount(revenue.refunded.count)} refund(s)`}
            valueTestId="admin-revenue-tile-refunded"
          />
        </dl>
        <div className="flex flex-wrap gap-6">
          <span data-testid="admin-revenue-paid" className="l2f-tabular font-mono text-[13px] text-ink-muted">
            paid {formatMoney(revenue.paid.total, revenue.currency)}
          </span>
          <span data-testid="admin-revenue-due" className="l2f-tabular font-mono text-[13px] text-ink-muted">
            due {formatMoney(revenue.due.total, revenue.currency)}
          </span>
          <span data-testid="admin-revenue-refunded" className="l2f-tabular font-mono text-[13px] text-ink-muted">
            refunded {formatMoney(revenue.refunded.total, revenue.currency)}
          </span>
        </div>
      </Panel>

      <Panel
        title="Subscriptions"
        subtitle="The active subscriptions behind MRR, valued at catalog list price. Scale is negotiated and excluded."
        testId="admin-revenue-subscriptions"
        actions={
          <AppLinkButton to="/admin/plans" variant="outline" size="sm">
            Plans & pricing
          </AppLinkButton>
        }
      >
        {subscriptions === null ? (
          <LoadingPanel label="Loading subscriptions" rows={3} />
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <MetricTile
                label="MRR"
                value={formatCompactMoney(summaryState.data?.revenue.mrr ?? 0, currency)}
                valueTitle={formatMoney(summaryState.data?.revenue.mrr ?? 0, currency)}
                hint={`${formatCount(subscriptions.activeTotal)} active`}
                hintTone="accent"
                valueTestId="admin-revenue-tile-mrr"
              />
              <MetricTile
                label="Lifter premium"
                value={formatCompactCount(subscriptions.lifterPremium)}
                valueTitle={formatCount(subscriptions.lifterPremium)}
                hint="incl. live trials"
              />
              <MetricTile
                label="Paid coaching"
                value={formatCompactCount(subscriptions.coachPaid)}
                valueTitle={formatCount(subscriptions.coachPaid)}
                hint="coach, gyms & gyms plus plans"
              />
              <MetricTile
                label="Lapsed / failed"
                value={formatCompactCount(subscriptions.lapsedPremium)}
                valueTitle={formatCount(subscriptions.lapsedPremium)}
                hint="premium windows that closed"
                hintTone={subscriptions.lapsedPremium > 0 ? 'warning' : 'success'}
              />
            </dl>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2" data-testid="admin-revenue-by-plan">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                  By catalog plan
                </span>
                {subscriptions.byPlan.map((entry) => (
                  <span
                    key={`${entry.audience}-${entry.planKey}`}
                    className="flex items-baseline justify-between gap-3 border-b border-hairline py-1 text-[12px]"
                  >
                    <span className="text-ink-muted">
                      {entry.label} <span className="uppercase tracking-[0.06em]">{entry.audience}</span>
                      {entry.priceMonthly > 0 ? ` · ${formatMoney(entry.priceMonthly, currency)}/mo` : ''}
                    </span>
                    <span className="l2f-tabular font-mono text-ink">
                      {formatCount(entry.count)} · {formatMoney(entry.mrr, currency)}
                    </span>
                  </span>
                ))}
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                  Who is premium
                </span>
                {premiumLifters.length === 0 ? (
                  <span className="text-[12px] text-ink-muted">No premium lifter account right now.</span>
                ) : (
                  premiumLifters.map((account) => (
                    <span key={account.userId} className="flex items-baseline justify-between gap-3 border-b border-hairline py-1 text-[12px]">
                      <Link to={`/admin/accounts/${account.userId}`} className="text-accent hover:text-accent-hover">
                        {account.displayName}
                      </Link>
                      <span className="l2f-tabular font-mono text-ink-muted">
                        {account.premiumUntil ? `until ${formatDate(account.premiumUntil)}` : 'premium tier'}
                      </span>
                    </span>
                  ))
                )}
                <span className="mt-2 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                  Who is on a paid coach plan
                </span>
                {paidBusinesses.length === 0 ? (
                  <span className="text-[12px] text-ink-muted">No business on a paid coaching plan.</span>
                ) : (
                  paidBusinesses.slice(0, 8).map((business) => (
                    <span key={business.userId} className="flex items-baseline justify-between gap-3 border-b border-hairline py-1 text-[12px]">
                      <Link to={`/admin/accounts/${business.userId}`} className="text-accent hover:text-accent-hover">
                        {business.displayName}
                      </Link>
                      <span className="l2f-tabular font-mono text-ink-muted">
                        {coachPlanLabel(business.plan)} · {formatCount(business.seatsUsed)} seats
                      </span>
                    </span>
                  ))
                )}
              </div>
            </div>

            <p className="text-[12px] text-ink-muted">
              Due now: <span className="font-bold text-warning">{formatMoney(revenue.due.total, revenue.currency)}</span>{' '}
              across {formatCount(revenue.due.count)} ledger row(s). Lapsed counts premium windows whose date has
              passed; the ledger has no "failed" status, so this is the honest proxy.
            </p>
          </>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="By period" subtitle="One row per calendar month with ledger activity." testId="admin-revenue-periods">
          {revenue.byPeriod.length ? (
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                  <th className="py-2 pr-3 font-bold">Period</th>
                  <th className="py-2 pr-3 font-bold">Paid</th>
                  <th className="py-2 pr-3 font-bold">Due</th>
                  <th className="py-2 pr-3 font-bold">Refunded</th>
                  <th className="py-2 font-bold">Rows</th>
                </tr>
              </thead>
              <tbody>
                {revenue.byPeriod.map((period) => (
                  <tr key={period.period} data-testid={`admin-revenue-period-${period.period}`} className="border-b border-hairline last:border-b-0">
                    <td className="l2f-tabular py-2 pr-3 font-mono text-[13px] font-bold text-ink">{period.period}</td>
                    <td className="l2f-tabular py-2 pr-3 font-mono text-[12px] text-success">{formatMoney(period.paid, revenue.currency)}</td>
                    <td className="l2f-tabular py-2 pr-3 font-mono text-[12px] text-warning">{formatMoney(period.due, revenue.currency)}</td>
                    <td className="l2f-tabular py-2 pr-3 font-mono text-[12px] text-ink-muted">{formatMoney(period.refunded, revenue.currency)}</td>
                    <td className="l2f-tabular py-2 font-mono text-[12px] text-ink-muted">{formatCount(period.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState
              title="No revenue in this range"
              body="No ledger row falls inside the selected dates. Widen the range to see the story."
              icon={<AlertIcon className="h-8 w-8 text-accent" />}
            />
          )}
        </Panel>

        <Panel title="By business" subtitle="The businesses that produced the money in this range." testId="admin-revenue-businesses">
          {revenue.byBusiness.length ? (
            <ul className="flex flex-col divide-y divide-hairline">
              {revenue.byBusiness.map((business) => (
                <li key={business.coachId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-[13px] font-bold text-ink">{business.displayName}</span>
                  <span className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-muted">
                    <span className="text-success">paid {formatMoney(business.paid, business.currency)}</span>
                    <span className="text-warning">due {formatMoney(business.due, business.currency)}</span>
                    <span>refunded {formatMoney(business.refunded, business.currency)}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No business activity"
              body="No coaching business has a ledger row in the selected range."
              icon={<AlertIcon className="h-8 w-8 text-accent" />}
            />
          )}
        </Panel>
      </div>
    </div>
  );
}
